import React, { useContext, useEffect, useRef } from "react";
import { saveUserProgress } from "../../services/saveUserProgress";
import { useParams } from "react-router-dom";

import UserProgressContext from "../../userProgressContext";
import MCQ from "./mcq";
import Slideshow from "./slideshow";
import convertToXDot01 from "../../utils/convertToXDot01";
import SuccessfulConfetti from "../Confetti/successfulConfetti";
import DialogueBatch from "../Narrative/DialogueBatch";
import DragDropPuzzle from "../DragDrop/DragDropPuzzle";
import BranchChoice from "../Narrative/BranchChoice";
import LevelSummary from "../Narrative/LevelSummary";
import SceneExplorer from "../Cinematic/SceneExplorer";
import AnomalyTriage from "../MiniGames/AnomalyTriage";
import MalwareQuarantine from "../MiniGames/MalwareQuarantine";
import SubstationTriage from "../MiniGames/SubstationTriage";
import InteractiveTerminal from "../MiniGames/InteractiveTerminal";
import PatchTheNetwork from "../MiniGames/PatchTheNetwork";
import UkraineReplay from "../MiniGames/UkraineReplay";
import CutsceneIntrusion from "./CutsceneIntrusion";

// Maps branch option consequence values to the grid node that should flip
const CONSEQUENCE_NODE_MAP = {
  grid_status_red:   'external',
  grid_status_amber: 'corporate',
};

// SubstationTriage zone → GridStatus node mapping
const ZONE_NODE_MAP = {
  safety_monitoring:  'ot',
  power_distribution: 'field',
  comms_tower:        'dmz',
  data_logging:       'corporate',
};
// SubstationTriage zone state → grid node state
const ZONE_STATE_TO_NODE_STATE = {
  offline:  'red',
  selected: 'amber',
  online:   'green',
};

/**
 * Normalise the raw dragdrop step object from any level JSON schema to the
 * props that DragDropPuzzle expects.
 *
 * Level JSONs may use either:
 *  - { type, zones, items, onComplete }  (categorise)
 *  - { type, slots[{id,label,accepts}], items, onComplete }  (place)
 *  - { variant, zones, items, … }  (canonical future form)
 */
function normalizeDragDrop(rawDD) {
  // Determine variant
  const variant =
    rawDD.variant ??
    (rawDD.type === 'categorise' ? 'categorise' : 'place-on-diagram');

  // Build zones: prefer rawDD.zones, else convert slots (pass x/y/w/h if present)
  const zones =
    rawDD.zones ??
    (rawDD.slots ?? []).map((s) => ({
      id:    s.id,
      label: s.label,
      ...(s.x !== undefined && { x: s.x, y: s.y, w: s.w, h: s.h }),
    }));

  // Build items: add correctZone from slots.accepts if missing
  const items = (rawDD.items ?? []).map((item) => ({
    ...item,
    correctZone:
      item.correctZone ??
      (rawDD.slots ?? []).find((s) => s.accepts === item.id)?.id,
  }));

  return { variant, zones, items };
}

const GameLevel = ({ script }) => {
  const { localProgress, setLocalProgress, flipNode, setNodeState, addCompletedMinigame, totalScore, setTotalScore, redeemedScores, setRedeemedScores, difficulty, name, emailId, finishedTasks } = useContext(UserProgressContext);
  const { levelId } = useParams();
  const totalScoreRef = useRef(totalScore);
  totalScoreRef.current = totalScore;

  const earnScore = (earned, stepKey) => {
    const points = Number(earned);
    if (!Number.isFinite(points)) return;
    const newTotal = parseInt(totalScoreRef.current) + points;
    totalScoreRef.current = newTotal;
    setTotalScore(newTotal);
    setRedeemedScores(prev => new Set([...prev, stepKey]));
    saveUserProgress({
      name,
      emailId,
      totalScore: newTotal,
      redeemedScores: new Set([...redeemedScores, stepKey]),
      finishedTasks,
    });
  };
  // console.log("localProgress @gameLevel:", localProgress);

  useEffect(() => {
    if (!script || !localProgress || !difficulty) return;
    const step = script[localProgress.toString()];
    if (!step || !('minigame' in step)) return;
    const mg = step.minigame;
    if (difficulty === 'beginner' && mg.type === 'interactive-terminal') {
      const stepKey = localProgress.toString();
      addCompletedMinigame(stepKey);
      if (mg.onComplete) {
        setLocalProgress(parseFloat(mg.onComplete).toFixed(2));
      } else {
        setLocalProgress(((localProgress * 10 + 0.01 * 10) / 10).toFixed(2));
      }
    }
  }, [localProgress, difficulty, script]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <React.Fragment>
      {localProgress &&
        localProgress > parseInt(levelId) &&
        script &&
        script[localProgress.toString()] && (
          <div className="w-full h-full flex flex-col">
            <div className="flex-none mb-4 md:mb-6">
              <h1
                className={`text-2xl md:text-5xl font-bold text-center${
                  [3, 5, 6, 7].includes(parseInt(levelId)) ? " animate-titleReveal" : ""
                }`}
              >
                Level {levelId}:{" "}
                {script[convertToXDot01(localProgress).toString()] &&
                  script[convertToXDot01(localProgress).toString()]["title"]}
              </h1>
            </div>
            <div className="grow">
              {"isLevelEnd" in script[localProgress.toString()] && (
                <SuccessfulConfetti />
              )}
              {("dialogues" in script[localProgress.toString()] ||
                ("character" in script[localProgress.toString()] &&
                  "dialogue" in script[localProgress.toString()])) && (
                <DialogueBatch key={localProgress} step={script[localProgress.toString()]} />
              )}
              {"question" in script[localProgress.toString()] && (() => {
                const stepKey = localProgress.toString();
                const stepScore = script[stepKey]["score"];
                const isReplay = redeemedScores.has(stepKey);
                const handleMCQCorrect = (!isReplay && stepScore != null)
                  ? () => earnScore(stepScore, stepKey)
                  : undefined;
                return (
                  <MCQ
                    key={localProgress}
                    question={script[stepKey]["question"]}
                    options={script[stepKey]["options"]}
                    onCorrectAnswer={handleMCQCorrect}
                  />
                );
              })()}
              {"slides" in script[localProgress.toString()] && (
                <Slideshow
                  slides={script[localProgress.toString()]["slides"]}
                  score={script[localProgress.toString()]["score"]}
                />

                // To integrate h5p content
                // <div className="flex justify-center w-full h-full">
                //   <ScriptInjector
                //     src={script[localProgress.toString()]["slides"]}
                //     position="#script-container"
                //   />
                //   <div
                //     id="script-container"
                //     className="flex justify-center w-full h-full"
                //   ></div>
                // </div>
              )}
              {"dragdrop" in script[localProgress.toString()] && (() => {
                const rawDD = script[localProgress.toString()].dragdrop;
                const { variant, zones, items } = normalizeDragDrop(rawDD);
                const handleDDComplete = rawDD.onComplete
                  ? () => setLocalProgress(parseFloat(rawDD.onComplete).toFixed(2))
                  : () => setLocalProgress(((localProgress * 10 + 0.01 * 10) / 10).toFixed(2));
                return (
                  <div data-step-type="dragdrop" className="w-full h-full">
                    <DragDropPuzzle
                      variant={variant}
                      items={items}
                      zones={zones}
                      prompt={rawDD.prompt}
                      score={script[localProgress.toString()].score}
                      onComplete={handleDDComplete}
                    />
                  </div>
                );
              })()}
              {"branches" in script[localProgress.toString()] && (
                <div data-step-type="branches" className="w-full h-full">
                  <BranchChoice
                    step={script[localProgress.toString()]}
                    onComplete={(reconvergeKey) =>
                      setLocalProgress(parseFloat(reconvergeKey).toFixed(2))
                    }
                    onWrongAnswer={(consequence) => {
                      const nodeId = CONSEQUENCE_NODE_MAP[consequence] ?? 'corporate';
                      flipNode(nodeId);
                    }}
                    onScoreUpdate={() => {
                      /* Score on branch steps handled by layout's localProgress watcher */
                    }}
                  />
                </div>
              )}
              {"summary" in script[localProgress.toString()] && (
                <div data-step-type="summary" className="w-full h-full">
                  <LevelSummary
                    step={script[localProgress.toString()]}
                    scoreDelta={0}
                    onComplete={() =>
                      setLocalProgress(((localProgress * 10 + 0.01 * 10) / 10).toFixed(2))
                    }
                  />
                </div>
              )}
              {"scene" in script[localProgress.toString()] && (
                <div data-step-type="scene" className="w-full">
                  <SceneExplorer
                    step={script[localProgress.toString()].scene}
                    onComplete={() =>
                      setLocalProgress(parseFloat(script[localProgress.toString()].scene.onComplete).toFixed(2))
                    }
                  />
                </div>
              )}
              {"cutscene" in script[localProgress.toString()] && (() => {
                const cs = script[localProgress.toString()].cutscene;
                return (
                  <CutsceneIntrusion
                    narration={cs.narration}
                    duration={cs.duration}
                    onComplete={
                      cs.onComplete
                        ? () => setLocalProgress(parseFloat(cs.onComplete).toFixed(2))
                        : () => setLocalProgress(((localProgress * 10 + 0.01 * 10) / 10).toFixed(2))
                    }
                  />
                );
              })()}
              {"minigame" in script[localProgress.toString()] && (() => {
                const mg = script[localProgress.toString()].minigame;
                const stepKey = localProgress.toString();
                const isReplay = redeemedScores.has(stepKey);
                const handleMgComplete = mg.onComplete
                  ? () => { addCompletedMinigame(stepKey); setLocalProgress(parseFloat(mg.onComplete).toFixed(2)); }
                  : () => { addCompletedMinigame(stepKey); setLocalProgress(((localProgress * 10 + 0.01 * 10) / 10).toFixed(2)); };
                const handleScoreUpdate = isReplay
                  ? () => {}
                  : (earned) => earnScore(earned, stepKey);
                if (mg.type === 'malwarequarantine') {
                  return (
                    <div data-step-type="minigame" className="w-full">
                      <MalwareQuarantine
                        files={mg.files ?? []}
                        score={mg.score ?? script[localProgress.toString()].score}
                        onComplete={handleMgComplete}
                        onScoreUpdate={handleScoreUpdate}
                        onWrongAction={() => {/* availability lesson handled by content */}}
                        isReplay={isReplay}
                      />
                    </div>
                  );
                }
                if (mg.type === 'anomalytriage') {
                  return (
                    <div data-step-type="minigame" className="w-full h-full">
                      <AnomalyTriage
                        step={mg}
                        onComplete={() => { setNodeState('ot', 'green'); handleMgComplete(); }}
                        onScoreUpdate={handleScoreUpdate}
                        onNodeFlip={flipNode}
                        isReplay={isReplay}
                      />
                    </div>
                  );
                }
                if (mg.type === 'SubstationTriage') {
                  return (
                    <div data-step-type="minigame" className="w-full h-full">
                      <SubstationTriage
                        onComplete={handleMgComplete}
                        onScoreUpdate={handleScoreUpdate}
                        isReplay={isReplay}
                        onZoneChange={(zoneId, newState) => {
                          const nodeId = ZONE_NODE_MAP[zoneId];
                          const nodeState = ZONE_STATE_TO_NODE_STATE[newState];
                          if (nodeId && nodeState) setNodeState(nodeId, nodeState);
                        }}
                      />
                    </div>
                  );
                }
                if (mg.type === 'interactive-terminal') {
                  return (
                    <div data-step-type="minigame" className="w-full h-full">
                      <InteractiveTerminal step={mg} onComplete={handleMgComplete} />
                    </div>
                  );
                }
                if (mg.type === 'PatchTheNetwork') {
                  return (
                    <div data-step-type="minigame" className="w-full h-full">
                      <PatchTheNetwork
                        step={mg}
                        onComplete={handleMgComplete}
                        onScoreUpdate={handleScoreUpdate}
                        isReplay={isReplay}
                      />
                    </div>
                  );
                }
                if (mg.type === 'UkraineIncidentReplay') {
                  return (
                    <div data-step-type="minigame" className="w-full h-full">
                      <UkraineReplay
                        step={mg}
                        onScoreUpdate={handleScoreUpdate}
                        onComplete={handleMgComplete}
                        isReplay={isReplay}
                      />
                    </div>
                  );
                }
                return (
                  <div data-step-type="minigame" className="p-4 text-slate-400 text-sm">
                    Unknown minigame type: {mg.type}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
    </React.Fragment>
  );
};

export default GameLevel;
